import RBACFlag from "@/app/components/core/RBACFlag";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import ClipboardComponent from "@/app/components/ui/Clipboard";
import CommentViewComponent from "@/app/components/ui/CommentViewComponent";
import NoRecordFoundComponent from "@/app/components/ui/NoRecordFound";
import BookingStatus from "@/app/components/ui/Reusable/BookingStatus";
import Breed from "@/app/components/ui/Reusable/Breed";
import PetName from "@/app/components/ui/Reusable/PetName";
import Species from "@/app/components/ui/Reusable/Species";
import StructuredTableSkeleton from "@/app/components/ui/Skeletons/StructuredTableSkeleton";
import TimerComponent from "@/app/components/ui/TimerComponent";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useMappedConstants } from "@/app/hooks/useMappedConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useUtils } from "@/app/hooks/useUtils";
import { ROUTES } from "@/app/routes";
import { IPatientWaitingListState } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";
import RupeeImage from "@assets/images/rupee.svg";
import { PhoneIcon, ShoppingCartIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import check from "@assets/images/check.svg";
import { LuClock3 } from "react-icons/lu";
import { TbExternalLink } from "react-icons/tb";
import { useNavigate } from "react-router";
import { Tooltip } from "react-tooltip";
import PWLActionModal, { IPWLActionModalState } from "@components/routes/modals/PWLAction";
import ThreeDotModal from "./ThreeDotModal";
import PWLAnalytics from "../PWLAnalytics";
import PatientWaitingListStats from "../PatientStatsList";
import PWLFiltersComponent from "../PWLFilters";
import ToggleWithIcon from "@/app/components/ui/Toggles/ToggleWithIcon";
import { usePWLEffect } from "@/app/hooks/usePWLEffect";
import PaginationBar from "@/app/components/ui/Pagination/PaginationBar";
import useRefetchPWL from "@/app/hooks/api_hooks/process/useReFetchPWL";
import { PWL_DEFAULT_PAGE_SIZE, PWL_PAGE_SIZE_OPTIONS } from "@/api_framework/api_modals/Patients";
import { useNewCartAPI } from "@/app/hooks/api_hooks/Cart/useNewCartAPI";
import { useCartProfilePinGate } from "@/app/hooks/useCartProfilePinGate";
import CartProfilePinModal from "@/app/components/routes/modals/CartProfilePinModal";
import { useBookingSummaryAPI } from "@/app/hooks/api_hooks/bookings/useBookingSummaryAPI";
import { useCreateEMRAPI } from "@/app/hooks/api_hooks/emr/useCreateEMRAPI";
import { useV2EMRListAPI } from "@/app/hooks/api_hooks/emr/useV2EMRListAPI";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import type { IBookingSummaryResponseData } from "@/api_framework/api_modals/Bookings";
import type { IPatientWaitingListData } from "@/api_framework/api_modals/Patients";
import ButtonCore from "@/app/components/ui/Buttons/ButtonCore";
import { appointmentConfig } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { clsx } from "clsx";
import PetBirthdayIcon from "@/app/components/ui/Reusable/PetBirthdayIcon";
import HighRiskIcon from "@/app/components/ui/Reusable/HighRiskIcon";
import {
  findRecentlyCompletedEMRForPet,
  isActiveEMRForPet,
  shouldAutoStartVoiceFromPWL,
  shouldVerifyVoiceFromPWL,
  VOICE_PARAM_START,
  VOICE_PARAM_VERIFY,
  PWL_VOICE_INFLIGHT,
} from "@/app/utils/voiceAutoStart";
import { useVoiceSession, MAX_CONCURRENT_VOICE_SESSIONS } from "@/app/context/VoiceSessionContext";
import { useVoiceEMRClickLog } from "@/app/hooks/useVoiceEMRClickLog";


const ROWS_PER_PAGE = 50;

// The PWL route can unmount while draft lookup/creation is running. This module
// lock prevents a repeated click or route remount from creating another draft.
const voiceEMRStartsInFlight = PWL_VOICE_INFLIGHT;

const PWLTable = () => {
  const { NO_PWL_RESULTS } = useImageConstants();

  const { convertTime12HrFormat, getDisplayStaffName } = useUtils();
  // const { appointmentButtonMapping, appointmentStatusMapping, appointmentActionDescriptions } = useMappedConstants();
  // RBAC: PWL_SALES_TEAM_VIEW — legacy roles: sales_ops, sales_admin, central_sales_lead
  const isBD = useFeatureAccess("PWL_SALES_TEAM_VIEW");
  // RBAC: PWL_DOCTOR_VIEW — legacy role: doctor
  const isDoctor = useFeatureAccess("PWL_DOCTOR_VIEW");
  // RBAC: PWL_SUPER_ADMIN_VIEW — legacy role: super_admin
  const isSuperAdmin = useFeatureAccess("PWL_SUPER_ADMIN_VIEW");
  const canAccessEMR = isDoctor || isSuperAdmin;
  const canShowCartIcon = !(isDoctor || isBD);
  const routeId = ROUTES.ROOT.children.PATIENT_WAITING_LIST.id;
  const isPWLLoading = useLoader("isPWLInProgress");
  const { routeState } = useRouteInfo(routeId);
  const { waitingList, waitingListPagination } = routeState.state as IPatientWaitingListState;
  const refetchPWL = useRefetchPWL();

  // What the server actually served, not what was last clicked, so the controls
  // can never show one page while the table shows another.
  const currentPage = waitingListPagination?.page ?? 1;
  const pageSize = waitingListPagination?.limit ?? PWL_DEFAULT_PAGE_SIZE;
  const totalRows = waitingListPagination?.total ?? waitingList?.length ?? 0;

  const [searchParams, setSearchParams] = useSearchParams();


  const goToPage = (page: number, limit: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("page", String(page));
    nextParams.set("limit", String(limit));
    setSearchParams(nextParams, { replace: true });

    refetchPWL({ page, limit });
  };
  const hasListData = Array.isArray(waitingList) && waitingList.length > 0;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loggedInUserEmail = useAppSelector((state) => state.auth?.user?.email);
  const loggedInUserRole = useAppSelector((state) => state.auth?.userPermissions?.role_name);
  const orgServices = useAppSelector((state) => state.staticAPI?.org_services);

  // PWL rows only carry service_id, so the service type (IPD vs OPD etc.) has
  // to come from the org services list cached at app bootstrap.
  const serviceTypeIdByServiceId = useMemo(() => {
    const map = new Map<string, string>();
    (orgServices ?? []).forEach((service: any) => {
      if (service?.service_id && service?.service_type_id) {
        map.set(String(service.service_id), String(service.service_type_id));
      }
    });
    return map;
  }, [orgServices]);

  const { setVisible: setPaymentVisible, setModalData: setPaymentModalData } = useModalActions("BOOKINGS_PAYMENT_STATUS");

  const [open, setOpen] = useState(false);
  const [actionModalData, setActionModalData] = useState<IPWLActionModalState | null>(null);
  const [bookingSummaryData, setBookingSummaryData] = useState<IBookingSummaryResponseData | null>(null);

  const [stat, setStat] = useState<string>("Total");
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);

  const { execute: createNewCart } = useNewCartAPI();
  const { runWithPinGate, pinModalProps } = useCartProfilePinGate();
  const [cartPinCheckBookingId, setCartPinCheckBookingId] = useState<string | null>(null);
  const { execute: fetchBookingSummary, isBookingSummaryInProgress } = useBookingSummaryAPI();
  const { execute: createDraftEMR } = useCreateEMRAPI();
  const { execute: fetchEmrList } = useV2EMRListAPI();
  const {
    startSession: startVoiceSession,
    setActiveSession: setActiveVoiceSession,
    sessions: voiceSessions,
    hasSessionForPet: hasVoiceSessionForPet,
  } = useVoiceSession();
  const logVoiceEMR = useVoiceEMRClickLog();

  const { statusChipPWL } = useUtils();

  const [clientFilter, setClientFilter] = useState<"all" | "prepaid" | "non_prepaid">("all");

  const [emrFiler, setEmrFilter] = useState<"all" | "created" | "completed" | "no_action">("all");
  const [hasRequestedListAtLeastOnce, setHasRequestedListAtLeastOnce] = useState<boolean>(false);


  const handleAction = (data: any) => {
    const bookingId = data?.booking_id;
    setActionModalData(data);
    setBookingSummaryData(null);
    setOpen(true);
    if (bookingId) {
      fetchBookingSummary(bookingId).then((summaryData) => {
        setBookingSummaryData(summaryData);
      });
    }
  };

  usePWLEffect({
    withCacheActions: true,
  });


  const handleRowSelectTonewTab = (value) => {
    const userId = value?.user_id;
    const petId = value?.pet_id;
    window.open(`/app/customer/profile/customer-details/EMR/${userId}/${petId}`, "_self");
  };

  const getEMRStatusForTemplatePage = (emrStatus?: string | null) => {
    const normalized = emrStatus?.toLowerCase()?.trim();
    if (!normalized) return "DRAFT";
    if (normalized === "created") return "DRAFT";
    if (normalized === "completed") return "COMPLETED";
    return emrStatus.toUpperCase();
  };

  const handleCreateEMR = async (wl: IPatientWaitingListData) => {
    const userId = wl?.user_id;
    const petId = wl?.pet_id;

    if (!userId || !petId) {
      toast.error("Missing user/pet details for EMR creation.");
      return;
    }

    logVoiceEMR("CREATE_EMR_BUTTON_CLICKED", "Create EMR", {
      scr_id: "PWL",
      client_id: userId,
      pet_id: petId,
      booking_id: wl?.booking_id,
      clinic_id: wl?.clinic_details?.id,
    });

    // Voice-qualifying doctors get the floating recorder started here too, exactly
    // like clicking the patient name. It runs in this same tab so the recorder
    // (which only lives in the tab that owns the mic) follows the navigation.
    const qualifiesForVoice = shouldAutoStartVoiceFromPWL({
      email: loggedInUserEmail,
      roleName: loggedInUserRole,
      booking: wl,
      serviceTypeId: serviceTypeIdByServiceId.get(String(wl?.service_id)),
    });
    if (qualifiesForVoice) {
      const handoffKey = `${userId}:${petId}`;
      if (!voiceEMRStartsInFlight.has(handoffKey)) {
        void startVoiceSessionFromPWL(wl, handoffKey, "Patient row");
      }
    }

    // The row says neither Waiting nor Attending, but PWL rows come from a cached list. Hand the
    // decision to the EMR tab, which re-reads the live booking status and only then
    // runs the same create-draft + start-recording flow.
    const needsVoiceVerification =
      !qualifiesForVoice &&
      shouldVerifyVoiceFromPWL({
        email: loggedInUserEmail,
        roleName: loggedInUserRole,
        booking: wl,
      });

    // A `?voice=check` hand-off is a launch too: the EMR tab decides it from the
    // live booking, so a verify turned down there still has a launch to divide by.
    if (needsVoiceVerification) {
      logVoiceEMR("VOICE_EMR_PWL_LAUNCH_CLICKED", "Patient row", {
        event_action: "pwl_verify",
        client_id: userId,
        pet_id: petId,
      });
    }

    // Same tab (not a new window): keeps the floating voice recorder alive, which
    // only persists within the tab that owns the mic.
    navigate(
      `/app/customer/profile/customer-details/EMR/${userId}/${petId}${
        needsVoiceVerification ? `?voice=${VOICE_PARAM_VERIFY}` : ""
      }`,
    );
  };

  const startVoiceSessionFromPWL = async (
    wl: IPatientWaitingListData,
    handoffKey: string,
    trigger: "Patient row" | "Patient name",
  ) => {
    const customerId = wl?.user_id;
    const petId = wl?.pet_id;
    if (!customerId || !petId) return;

    const logLaunch = (
      event_type: string,
      cta_text: string,
      extra: Parameters<typeof logVoiceEMR>[2] = {},
    ) => logVoiceEMR(event_type, cta_text, { client_id: customerId, pet_id: petId, ...extra });

    logLaunch("VOICE_EMR_PWL_LAUNCH_CLICKED", trigger, { event_action: "pwl_autostart" });

    if (hasVoiceSessionForPet(petId)) {
      const existing = voiceSessions.find((s) => s.petId === petId);
      if (existing) setActiveVoiceSession(existing.emrId);
      logLaunch("VOICE_EMR_AUTOSTART_SKIPPED", "Already recording", {
        event_action: "already_recording",
        emr_id: existing?.emrId,
      });
      return;
    }
    if (voiceSessions.length >= MAX_CONCURRENT_VOICE_SESSIONS) {
      toast.error(
        `You already have ${MAX_CONCURRENT_VOICE_SESSIONS} voice recordings open — finish or discard one before starting another.`,
      );
      logLaunch("VOICE_EMR_AUTOSTART_SKIPPED", "Session cap reached", {
        event_action: "session_cap",
      });
      return;
    }

    voiceEMRStartsInFlight.add(handoffKey);
    try {
      const emrList = await fetchEmrList(customerId, petId);
      if (!Array.isArray(emrList)) {
        throw new Error("Couldn't check existing EMR drafts.");
      }

      const recentlyCompleted = findRecentlyCompletedEMRForPet(emrList, petId);
      if (recentlyCompleted) {
        logLaunch("VOICE_EMR_AUTOSTART_SKIPPED", "Visit already written up", {
          event_action: "recently_completed_emr",
          emr_id: recentlyCompleted?.emr_id,
        });
        return;
      }

      const bookingEmrStatus = String(wl?.emr_details?.emr_status ?? "").trim().toLowerCase();
      let emrId = ["created", "draft"].includes(bookingEmrStatus)
        ? wl?.emr_details?.emr_id
        : undefined;

      if (!emrId) {
        const latestDraft = emrList
          .filter((emr) => isActiveEMRForPet(emr, petId) && emr?.created_by === loggedInUserEmail)
          .sort((left, right) =>
            (right?.updated_at ?? right?.created_at ?? 0) - (left?.updated_at ?? left?.created_at ?? 0),
          )[0];

        emrId = latestDraft?.emr_id;
      }

      if (!emrId) {
        const response = await createDraftEMR(customerId, petId, wl?.clinic_details?.id, true);
        emrId = response?.emr_id;

        // The active draft can be created between the first lookup and our create
        // call. When the server rejects that race, re-query and reuse its EMR.
        if (!emrId) {
          const refreshedEMRs = await fetchEmrList(customerId, petId);
          const activeEMR = Array.isArray(refreshedEMRs)
            ? refreshedEMRs
                .filter((emr) => isActiveEMRForPet(emr, petId) && emr?.created_by === loggedInUserEmail)
                .sort((left, right) =>
                  (right?.updated_at ?? right?.created_at ?? 0) -
                  (left?.updated_at ?? left?.created_at ?? 0),
                )[0]
            : null;
          emrId = activeEMR?.emr_id;
        }
      }

      if (!emrId) {
        logLaunch("VOICE_EMR_AUTOSTART_SKIPPED", "No EMR to record into", {
          event_action: "no_emr_id",
        });
        toast.error("Couldn't create the EMR.");
        return;
      }

      startVoiceSession({
        emrId,
        userId: customerId,
        petId,
        patientName: wl?.pet_details?.name ?? wl?.user_details?.name,
        clinicId: wl?.clinic_details?.id,
        emrStatus: "DRAFT",
      });
      logLaunch("VOICE_EMR_AUTOSTART_STARTED", "Start recording", {
        emr_id: emrId,
        event_action: "mic_unchecked",
      });
    } catch (error) {
      logLaunch("VOICE_EMR_AUTOSTART_SKIPPED", "Could not start recording", {
        event_action: "error",
        source: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
      });
      toast.error(error instanceof Error ? error.message : "Couldn't start the voice recording.");
    } finally {
      voiceEMRStartsInFlight.delete(handoffKey);
    }
  };

  const handleViewEMR = (wl: IPatientWaitingListData) => {
    const userId = wl?.user_id;
    const petId = wl?.pet_id;
    const emrId = wl?.emr_details?.emr_id;

    if (!userId || !petId || !emrId) {
      toast.error("Missing user/pet/EMR details for EMR view.");
      return;
    }

    const status = getEMRStatusForTemplatePage(wl?.emr_details?.emr_status);

    if (status === "COMPLETED") {
      window.open(`${"/app/customer/profile/create-emr"}?emr_id=${emrId}&emr_status=${status}&user_id=${userId}&pet_id=${petId}`, "_blank");
      return;
    }

    window.open(`${"/app/customer/profile/create-emr"}?emr_id=${emrId}&emr_status=${status}&user_id=${userId}&pet_id=${petId}`, "_blank");
  };

  const handelGoToCart = async (wl: any) => {
    if (cartPinCheckBookingId !== null) return;
    setCartPinCheckBookingId(wl?.booking_id ?? "");
    await runWithPinGate(
      {
        userId: wl?.user_id,
        clinicId: wl?.clinic_details?.id ?? wl?.clinic_id,
        userMobileNumber: wl?.user_details?.mobile,
        petId: wl?.pet_id,
        source: "PWL",
      },
      () => openCartForBooking(wl),
    );
    setCartPinCheckBookingId(null);
  };

  const openCartForBooking = (wl: any) => {
    createNewCart(
      wl?.user_id,
      wl?.clinic_details?.id,
      wl?.clinic_details?.name,
      wl?.pet_id,
      wl?.service_details?.id,
      wl?.subservice_details?.id,
    ).then(({ cart_id }) => {
      if (cart_id) {
        const searchParams = new URLSearchParams({
          from_cart: "true",
          ...(wl?.pet_id ? { pet_id: wl.pet_id } : {}),
          ...(wl?.booking_id ? { booking_id: wl.booking_id } : {}),
        });
        window.open(`/app/user-cart/cart-creation/${wl?.user_id}/${cart_id}?${searchParams.toString()}`, "_blank");
      }
    });
  };

  const [isSticky, setIsSticky] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScrollForFilter = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY) {
        // Scrolling down → Hide sticky header
        setIsSticky(false);
      } else {
        // Scrolling up → Show sticky header
        setIsSticky(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScrollForFilter);
    return () => window.removeEventListener("scroll", handleScrollForFilter);
  }, [lastScrollY]);

  useEffect(() => {
    if (isPWLLoading || hasListData) {
      setHasRequestedListAtLeastOnce(true);
    }
  }, [isPWLLoading, hasListData]);

  const columns: TableColumn<IPatientWaitingListData>[] = [
    {
      id: "time",
      width: 80,
      header: "Time",
      className: "text-[0.75rem] font-semibold text-black",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span>{convertTime12HrFormat(row?.booking_start_time)}</span>
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      className: "text-[0.75rem] font-normal text-black",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => (
        <div className="flex">
          <span className={statusChipPWL(row?.booking_status)}>
            {row?.booking_status}
          </span>
        </div>
      ),
    },
    {
      id: "client",
      header: "Client",
      className: "text-[0.75rem] font-normal ",
      headerClassName: "font-semibold",
      filter: isDoctor
        ? undefined
        : {
            options: [
              { label: "All", value: "all" },
              { label: "Prepaid Users", value: "prepaid" },
              { label: "Non-Prepaid Users", value: "non_prepaid" },
            ],
            value: clientFilter,
            allValue: "all",
            onChange: (next) => setClientFilter(next as "all" | "prepaid" | "non_prepaid"),
            filterFn: (row, value) => {
              if (value === "prepaid") return Number(row?.booking_fee_amount) > 0;
              if (value === "non_prepaid") return !(Number(row?.booking_fee_amount) > 0);
              return true;
            },
          },
      cell: ({ row }) => {
        // This appointment qualifies for the floating voice recorder: an
        // allowlisted doctor, Waiting/Attending, AND the logged-in doctor is
        // this booking's assigned provider.
        const qualifiesForVoice = !!(
          shouldAutoStartVoiceFromPWL({
            email: loggedInUserEmail,
            roleName: loggedInUserRole,
            booking: row,
            serviceTypeId: serviceTypeIdByServiceId.get(String(row?.service_id)),
          }) &&
          row?.user_id &&
          row?.pet_id
        );

        // The row is not Waiting/Attending, but it was drawn from a cached list that may
        // have moved on. For an allowlisted doctor the EMR tab re-reads the live
        // booking status (`?voice=check`) instead of writing the recording off here.
        const needsVoiceVerification = !!(
          !qualifiesForVoice &&
          shouldVerifyVoiceFromPWL({
            email: loggedInUserEmail,
            roleName: loggedInUserRole,
            booking: row,
          }) &&
          row?.user_id &&
          row?.pet_id
        );

        // Doctors always land on the pet's EMR tab; everyone else keeps the
        // appointments view. `?voice=1` (only when voice-qualifying) makes the EMR
        // tab auto-start the recorder, so a right-click → "open in new tab" records
        // there too.
        const isDoctorRole = loggedInUserRole?.trim().toLowerCase() === "doctor";
        const voiceQuery = qualifiesForVoice
          ? `?voice=${VOICE_PARAM_START}`
          : needsVoiceVerification
            ? `?voice=${VOICE_PARAM_VERIFY}`
            : "";
        const nameLinkTo = isDoctorRole
          ? `/app/customer/profile/customer-details/EMR/${row?.user_id}/${row?.pet_id}${voiceQuery}`
          : `/app/customer/profile/customer-details/appointments/${row?.user_id}/${row?.pet_id}`;

        return (
        <div className="flex flex-col">
          <div className="flex items-start gap-0 min-w-0">
            <Link
              to={nameLinkTo}
              onClick={(event) => {
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                if (needsVoiceVerification) {
                  logVoiceEMR("VOICE_EMR_PWL_LAUNCH_CLICKED", "Patient name", {
                    event_action: "pwl_verify",
                    client_id: row?.user_id,
                    pet_id: row?.pet_id,
                  });
                }
                if (qualifiesForVoice) {
                  // Active EMRs are constrained per pet, so dedupe across every
                  // booking row for the same customer+pet combination.
                  const handoffKey = `${row.user_id}:${row.pet_id}`;
                  if (voiceEMRStartsInFlight.has(handoffKey)) return;

                  void startVoiceSessionFromPWL(row, handoffKey, "Patient name");
                }
              }}
              className="font-semibold text-black whitespace-normal break-words min-w-0 flex-1 cursor-pointer hover:underline"
            >
              {row?.user_details?.name ?? "-"}
            </Link>
          </div>
          <div className="flex items-center gap-0 text-[#1B68D3] font-semibold text-[0.75rem]">
            {row?.is_member ? (
              <span className="inline-flex items-center gap-1">
                <span>{row?.is_member}</span>
              </span>
            ) : null}
          </div>
        </div>
        );
      },
    },
    {
      id: "pet_details",
  
      header: "Pet Details",
      className: "text-[0.75rem] font-normal text-black whitespace-normal",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => {
        const isHighRisk = row?.pet_details?.high_risk;
        return (
          <div className="flex flex-col">
            {row?.pet_details?.has_birthday && (
              <div className="mb-1">
                <PetBirthdayIcon
                  hasBirthday={row?.pet_details?.has_birthday}
                  petName={row?.pet_details?.name}
                  className="!ml-0"
                />
              </div>
            )}
            {isHighRisk && (
              <div className="mb-1">
                <HighRiskIcon isHighRisk={isHighRisk} className="!ml-0" />
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="font-semibold">
                {row?.pet_details?.name ?? "-"}
              </span>
            </div>
            <span className="text-[#727272] font-semibold text-[0.75rem]">
              <Species className="" speciesId={row?.pet_details?.pet_species_type_id} />
              <span> - </span>
              <Breed
                className="text-[#727272] font-semibold text-[0.75rem] whitespace-normal break-words"
                breedId={row?.pet_details?.pet_breed_id}
                speciesId={row?.pet_details?.pet_species_type_id}
                petBreed={row?.pet_details?.pet_breed}
              />
            </span>
          </div>
        )
      },
    },
    {
      id: "service_provider",
      header: "Service",
      className: "text-[0.75rem] text-black whitespace-normal",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span
            className="font-semibold"
          >
            {row?.service_details?.service_type ?? "-"}
          </span>
          <span
            className="text-[#727272] font-semibold text-[0.75rem] whitespace-normal break-words"
          >
            {row?.subservice_details?.name ?? "-"}
            {getDisplayStaffName(row) ? ` - ${getDisplayStaffName(row)}` : ""}
          </span>
        </div>
      ),
    },
    {
      id: "comments",
      header: "Comment",
      className: "text-[0.75rem] font-normal text-black whitespace-normal",
      headerClassName: "text-[0.875rem] text-center font-semibold text-[#193A6A]",
      cell: ({ row }) => (
        <div className="flex flex-col max-w-[320px] break-words">
          {row?.appointment_note !== null && row?.appointment_note !== "<ol><li></li></ol>" ? (
            <CommentViewComponent appointmentNotes={row?.appointment_note} />
          ) : (
            <span className="text-[#808DA0] text-center">-</span>
          )}
        </div>
      ),
    },
    {
      id: "vaccination",
      header: "Vaccination",
      className: "text-[0.75rem] font-normal text-black",
      headerClassName: "text-[0.875rem] font-semibold text-center text-[#193A6A]",
      cell: ({ row }) => (
        <div className="flex flex-col">
          {row?.vaccination === "over_due" && <p className="font-semibold text-[#EF0303] text-center">Overdue</p>}
          {row?.vaccination === "upcoming" && <p className="font-semibold text-[#E0A200] text-center">Upcoming</p>}
          {row?.vaccination === "missing_data" && <p className="font-semibold text-[#000] text-center">Missing Data</p>}
          {row?.vaccination === "all_done" && <p className="font-semibold text-black text-center">Up To Date</p>}
          {!row?.vaccination && <span className="text-[#808DA0] text-center">-</span>}
        </div>
      ),
    },
    ...(canAccessEMR
      ? ([
        {
          id: "emr",
          header: "EMR",
          width: 120,
          className: "text-[0.75rem] font-normal text-black",
          headerClassName: "text-[0.875rem] font-semibold text-[#193A6A] text-center",
          filter: {
            options: [
              { label: "All", value: "all" },
              { label: "Completed", value: "completed" },
              { label: "Created", value: "created" },
              { label: "No Action Taken", value: "no_action" },
            ],
            value: emrFiler,
            allValue: "all",
            onChange: (next) => setEmrFilter(next as "all" | "completed" | "created" | "no_action"),
            filterFn: (row, value) => {
              const status = String(row?.emr_details?.emr_status ?? "")
                .trim()
                .toLowerCase();

              if (value === "completed") return status === "completed";
              if (value === "created") return status === "created";
              if (value === "no_action") return !status || status === "no action taken" || status === "no_action";
              return true;
            },
          },
          cell: ({ row }) => (
            <div className="flex items-center justify-center text-center" data-table-stop-row-click="true">
              {row?.emr_details ? (<span
                className={clsx(
                  "text-[0.75rem] font-semibold",
                  {
                    "text-[#166534]": row?.emr_details?.emr_status === "Completed",
                    "text-[#EF0303]": row?.emr_details?.emr_status === "No Action Taken",
                    "text-[#1B68D3]": row?.emr_details?.emr_status === "Created",
                  }
                )}
              >
                {row?.emr_details?.emr_status}
              </span>) :
                (<span className="text-[#EF0303] text-[0.75rem] font-semibold">No Action Taken</span>)
              }
            </div>
          ),
        } satisfies TableColumn<IPatientWaitingListData>,
      ] as TableColumn<IPatientWaitingListData>[])
      : []),
    ...(!isBD && !isDoctor
      ? ([
        {
          id: "app",
          header: "App",
          width: 70,
          className: "text-[0.75rem] font-normal text-black",
          headerClassName: "text-[0.875rem] font-semibold text-[#193A6A] text-center",
          cell: ({ row }) => (
            <div className="flex flex-col">
              {row?.isappdownloaded ? (
                <span className="text-center">
                  <img src={check} alt="Checked" className="w-4 h-4 inline-block" />
                </span>
              ) : (
                <span className="font-semibold text-center">No</span>
              )}
            </div>
          ),
        } satisfies TableColumn<IPatientWaitingListData>,
      ] as TableColumn<IPatientWaitingListData>[])
      : []),
    {
      id: "action",
      header: "Action",
      overflow: "visible",
      className:
        "text-[0.75rem] font-normal text-black whitespace-nowrap",
      headerClassName:
        "text-[0.875rem] text-center font-semibold text-[#193A6A]",
      cell: ({ row }) => (
        <div className="flex items-center w-full gap-[10px] pl-0" data-table-stop-row-click="true">
          <div className="flex items-center gap-[20px]">
            {isDoctor ? (
              (row?.emr_details?.emr_status === "Created" || row?.emr_details?.emr_status === "Completed") ? (
                <ButtonCore size="PWL" type="success" onButtonClick={() => handleViewEMR(row)}>
                  View EMR
                </ButtonCore>
              ) : (
                <ButtonCore size="PWL" type="primary" onButtonClick={() => handleCreateEMR(row)}>
                  Create EMR
                </ButtonCore>
              )
            ) : (
              <>
                {appointmentConfig[row?.booking_status?.toLowerCase()]?.label !== "" ? (
                  <RBACFlag actionId="ACTION_BUTTON_PWL">
                    <ButtonCore size="PWL" type={appointmentConfig[row?.booking_status?.toLowerCase()]?.type} onButtonClick={() => handleAction(row)}>
                      {appointmentConfig[row?.booking_status?.toLowerCase()]?.label}
                    </ButtonCore>
                  </RBACFlag>
                ) : (
                  <p className="w-20"></p>
                )}
              </>
            )}

            {canShowCartIcon && cartPinCheckBookingId === (row?.booking_id ?? "") ? (
              <div className="relative p-1 text-left rtl:text-right ">
                <div role="status">
                  <svg
                    aria-hidden="true"
                    className="inline h-5 w-5 animate-spin fill-blue-600 text-gray-200"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : canShowCartIcon ? (
              <div onClick={() => handelGoToCart(row)} data-table-stop-row-click="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M16 16C16.5304 16 17.0391 16.2107 17.4142 16.5858C17.7893 16.9609 18 17.4696 18 18C18 18.5304 17.7893 19.0391 17.4142 19.4142C17.0391 19.7893 16.5304 20 16 20C15.4696 20 14.9609 19.7893 14.5858 19.4142C14.2107 19.0391 14 18.5304 14 18C14 16.89 14.89 16 16 16ZM0 0H3.27L4.21 2H19C19.2652 2 19.5196 2.10536 19.7071 2.29289C19.8946 2.48043 20 2.73478 20 3C20 3.17 19.95 3.34 19.88 3.5L16.3 9.97C15.96 10.58 15.3 11 14.55 11H7.1L6.2 12.63L6.17 12.75C6.17 12.8163 6.19634 12.8799 6.24322 12.9268C6.29011 12.9737 6.3537 13 6.42 13H18V15H6C5.46957 15 4.96086 14.7893 4.58579 14.4142C4.21071 14.0391 4 13.5304 4 13C4 12.65 4.09 12.32 4.24 12.04L5.6 9.59L2 2H0V0ZM6 16C6.53043 16 7.03914 16.2107 7.41421 16.5858C7.78929 16.9609 8 17.4696 8 18C8 18.5304 7.78929 19.0391 7.41421 19.4142C7.03914 19.7893 6.53043 20 6 20C5.46957 20 4.96086 19.7893 4.58579 19.4142C4.21071 19.0391 4 18.5304 4 18C4 16.89 4.89 16 6 16ZM15 9L17.78 4H5.14L7.5 9H15Z" fill="#687183" />
                </svg>
              </div>
            ) : null}
          </div>

          <div className="ml-auto" data-table-stop-row-click="true">
            <ThreeDotModal bookingDetails={row} />
          </div>
        </div>
      ),
    },
  ];

  const getRowBgClassName = (wl: IPatientWaitingListData) =>
    wl?.booking_status?.toLowerCase() === "completed" && wl?.invoice_details === null ? "bg-[#FFEBEB]" : "bg-white";

  const shouldShowTableLoader = isPWLLoading || (!hasRequestedListAtLeastOnce && !hasListData);

  return (
    <>
      <div className="  bg-white ">
        <div
          className={`relative px-2`}
        >
          <h3 className="text-[1rem] lg:text-[1.125rem] text-black font-semibold mb-2 sm:mb-2 2xl:mb-4">
            Patient Waiting List
          </h3>
          {showAnalytics ? <PWLAnalytics /> : <PatientWaitingListStats currentStat={stat} setStatCard={setStat} />}
          <div className=" mt-2">
            <PWLFiltersComponent setStatCard={setStat} withStats={true} currentStat={stat} cacheFilters={true} />
          </div>
          {/* <div
            data-tooltip-id="show_analytics"
            data-tooltip-content="Show Analytics"
            className="absolute top-2 right-2  justify-start items-center gap-x-2 hidden sm:flex"
          >
            <p>Show Analytics</p>
            <ToggleWithIcon
              toggleState={showAnalytics}
              onToggleChange={(status) => {
                setShowAnalytics(status);
              }}
            />
          </div> */}
          <Tooltip id="show_analytics" style={{ fontSize: "10px" }} />
        </div>

        {!shouldShowTableLoader ? (
          hasListData ? (
            <div className="relative mt-6 ml-[10px]">
              <TableComponent
                columns={columns}
                data={waitingList}
                getRowId={(wl) => `${wl?.user_id}-${wl?.pet_id}-${wl?.booking_id}`}
                rowClassName={(wl, _rowIndex, ctx) => {
                  const base = `cursor-pointer ${getRowBgClassName(wl)}`;
                  if (!ctx?.canExpand) return base;
                  return ctx.isExpanded ? "cursor-pointer bg-[#EEF5FF]" : base;
                }}
                containerClassName=""
                scrollContainerClassName="mt-0 max-h-[calc(100vh-260px)] overflow-y-auto overflow-x-auto"
                minTableWidthClassName="min-w-full"
                tableClassName="rounded-[20px]"
                bodyClassName="text-xs"
                expandable={{
                  expandOnRowClick: true,
                  showToggleInFirstColumn: false,
                  canExpandRow: () => true,
                  expandedRowClassName: "bg-[#F7FAFF]",
                  renderExpandedRowCells: (wl) => {

                    const hasPaid = wl?.booking_fee_amount != null && wl?.booking_fee_amount > 0;

                    const waitingNode = (
                      <div className="flex items-center  justify-left gap-[5px] whitespace-normal break-words">
                        <span className="text-[#727272] text-[0.75rem] font-semibold">Waiting Time</span>
                        <span className="font-semibold text-black">
                          {wl?.meta_data?.wait_timer ? <TimerComponent initialTime={wl?.meta_data?.wait_timer} /> : "NA"}
                        </span>
                      </div>
                    );

                    const createdByNode = (
                      <div className="flex items-center gap-[5px] justify-start whitespace-normal break-words">
                        <span className="text-[#727272] text-[0.75rem] font-semibold">Created By</span>
                        <span className="font-semibold text-black whitespace-normal break-words">{wl?.created_by ?? "NA"}</span>
                      </div>
                    );

                    const userNode = (
                      <div className="flex items-center gap-[5px] whitespace-normal justify-start break-words">
                        <span className="text-[#727272] text-[0.75rem] font-semibold">User</span>
                        <span className="font-semibold text-black">{wl?.user_details?.customer_type || "NA"}</span>
                      </div>
                    );

                    const invoiceNode = (
                      <div className="flex items-start justify-center gap-[5px] whitespace-normal break-words">
                        <span className="text-[#727272] text-[0.75rem] font-semibold">Invoice</span>
                        <span className="font-semibold text-black flex flex-col gap-1">
                          {wl?.invoice_details?.map((invoice, i) => {
                            const fileName = invoice?.file_name || "invoice.pdf";
                            const shortName = "Invoice " + (i + 1);

                            return (
                              <a
                                key={i}
                                href={invoice?.invoice_link}
                                target="_blank"
                                className="text-[#0068FF] text-[0.75rem] hover:underline whitespace-nowrap font-semibold"
                                title={fileName}
                              >
                                {shortName}
                              </a>
                            );
                          })}
                        </span>
                      </div>
                    );

                    const prepaidNode = (
                      <div className="flex items-center justify-left gap-[5px] whitespace-normal break-words">
                        {hasPaid && <img src={RupeeImage} alt="" aria-hidden="true" className="w-4 h-4 shrink-0 leading-none" />}
                        <span className="text-[#727272] text-[0.75rem] font-semibold">Prepaid</span>
                        <span className="font-semibold text-black">
                          {hasPaid ? "Yes" : "No"}
                        </span>
                        {hasPaid && (
                          <a
                            href="#"
                            className="underline text-xs text-black"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setPaymentModalData({
                                bookingDetails: {
                                  booking_id: wl?.booking_id,
                                },
                              });
                              setPaymentVisible();
                            }}
                          >
                            Show Payment History
                          </a>
                        )}
                      </div>
                    )

                    const baseCells: ReactNode[] = [
                      ...(!isDoctor ? [prepaidNode] : []),
                      waitingNode,
                      null,
                      userNode,
                      createdByNode,
                      invoiceNode
                    ];
                    return [...baseCells];
                  },
                }}
              />

              <Tooltip id="user_detail_name" place="bottom" />
              <Tooltip id="service_detail_name" place="bottom" />
              <Tooltip id="staff_detail_name" place="bottom" />
              <Tooltip id="created_by" place="bottom" />
              <Tooltip id="comment_details2" place="bottom" />
              <Tooltip id="clinic_details_dispclay_name" place="bottom" />
            </div>
          ) : (
            <NoRecordFoundComponent
              imageURL={NO_PWL_RESULTS}
              message="No Appoinments Found"
              description="No appointments found for the given filters, try something else"
            />
          )
        ) : (
          <StructuredTableSkeleton columns={columns.length} className="mt-6 ml-[10px]" />
        )}

        {!shouldShowTableLoader && (
          <PaginationBar
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalRows}
            pageSizeOptions={PWL_PAGE_SIZE_OPTIONS}
            emptyLabel="No appointments"
            onPageChange={goToPage}
          />
        )}
      </div>
      {open && (
        <PWLActionModal
          open={open}
          setOpen={setOpen}
          actionModalData={actionModalData}
          bookingSummaryData={bookingSummaryData}
          isBookingSummaryInProgress={isBookingSummaryInProgress}
        />
      )}
      <CartProfilePinModal {...pinModalProps} />
    </>
  );
};

export default PWLTable;
